import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import { SVButton } from '../core';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';
import {
  useCopyBrandingTheme,
  useDeleteBrandingTheme,
} from '@/hooks/requests/useBrandingThemes';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';

type BrandingThemeCopyDeleteDialogProps = {
  uuid: string;
  name: string;
  isDelete: boolean;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shouldNavigate: boolean;
};

export default function BrandingThemeCopyDeleteDialog({
  uuid,
  name,
  isDelete,
  open,
  setOpen,
  shouldNavigate,
}: BrandingThemeCopyDeleteDialogProps) {
  const navigate = useNavigate();
  const { copyBrandingTheme, isCopyBrandingThemePending } =
    useCopyBrandingTheme();
  const { deleteBrandingTheme, isDeleteBrandingThemePending } =
    useDeleteBrandingTheme();

  async function handleConfirm() {
    try {
      let copyUuid = '';
      if (isDelete) {
        await deleteBrandingTheme(uuid);
      } else {
        copyUuid = await copyBrandingTheme(uuid);
      }
      showToast({
        type: 'success',
        data: {
          content: `Branding theme successfully ${isDelete ? 'deleted' : 'copied'}`,
        },
      });
      setOpen(false);

      if (shouldNavigate) {
        if (isDelete) {
          navigate(ROUTES.BRANDING_THEMES);
        } else {
          navigate(`${ROUTES.BRANDING_THEME_VIEW_PARAMS}/${copyUuid}`);
        }
      }
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h6" component="h2">
          {isDelete ? 'Delete' : 'Copy'} Branding Theme?
        </Typography>
        <Typography sx={{ mt: 2 }}>
          Are you sure you want to {isDelete ? 'delete' : 'copy'} the "{name}"
          branding theme?
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="tertiary" onClick={() => setOpen(false)}>
          Close
        </SVButton>
        <SVButton
          isLoading={isCopyBrandingThemePending || isDeleteBrandingThemePending}
          onClick={handleConfirm}>
          Confirm
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
